import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { Button } from '../../../../shared/components/button/Button';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';
import DevolucaoFornecedorController from '../../../../controllers/DevolucaoFornecedorController';
import ClienteController from '../../../../controllers/ClienteController';
import { SelectAutoCompleteComponent } from '../../../../shared/components/SelectAutoCompleteComponent';
import { SelectAutoCompleteOptions } from '../../../../shared/types/selectAutoComplete';

/**
 * Tela 4 — compor a nota fiscal da devolução.
 *
 * Diálogo, e não rota nova, pelo mesmo motivo da tela 2: o operador chega aqui com os
 * registros já selecionados na tela 3. Uma rota nova o obrigaria a selecioná-los de novo.
 *
 * O QUE ESTA TELA NÃO É
 * Não é "espelhar e deixar ajustar". A medição de 29/10 (scripts 215 e 216) mostrou que o
 * valor do ICMS está gravado em 4 de 306 itens de nota de entrada: na prática ele não vem
 * da origem. Então os campos nascem com o que existe, a coluna "falta" diz o que não veio,
 * e o operador digita o resto. Campo vazio não é neutro quando o preenchimento certo é
 * conhecido — mas inventar valor é pior, então aqui a tela pede.
 *
 * A PRÉVIA É QUEM DECIDE SE DÁ PARA GERAR
 * Os impedimentos (`impede_*`) vêm resolvidos da procedure. Esta tela não reimplementa
 * regra nenhuma: ela desabilita o botão e MOSTRA O MOTIVO. Botão apagado sem explicação
 * só gera chamado.
 */

interface Props {
  aberto: boolean;
  dvfIds: number[];
  aoFechar: () => void;
  aoGerar: () => void;
}

interface ItemPrevia {
  dvf_id: number;
  prod_id: number;
  codigo_promatec?: string;
  produto?: string;
  nota_origem?: number;
  numero_item_origem?: number;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
  cfop_origem?: number;
  cfop_devolucao?: number;
  v_bc?: number | null;
  p_icms?: number | null;
  v_icms?: number | null;
  v_bc_st?: number | null;
  p_icms_st?: number | null;
  v_icms_st?: number | null;
  v_ipi?: number | null;
  /** Frete da nota de origem, rateado — sai em Outras Despesas Acessórias (script 238). */
  v_outro?: number | null;
  /** A descrição do item na nota de origem, que é a que sai na nota (script 238). */
  descricao_origem?: string | null;
  falta_o_que?: string;
  aviso_estoque?: string;
}

/** Os campos que o operador edita, por item. O resto da linha é só leitura. */
interface ItemEditavel {
  dvf_id: number;
  cfop: string;
  quantidade: number;
  v_bc: string;
  p_icms: string;
  v_icms: string;
  v_bc_st: string;
  p_icms_st: string;
  v_icms_st: string;
  v_ipi: string;
  v_outro: string;
}

const comoTexto = (valor: number | null | undefined): string =>
  valor === null || valor === undefined ? '' : String(valor);

/** Campo vazio vira null, e não zero: zero declarado não é o mesmo que campo ausente. */
const comoNumero = (texto: string): number | null => {
  const limpo = (texto ?? '').toString().replace(',', '.').trim();
  if (limpo === '') return null;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
};

/**
 * Monta as linhas editaveis a partir da previa, para a operacao escolhida.
 *
 * DEVOLUCAO espelha os tributos da nota de origem, que e a regra do contador.
 * SIMPLES REMESSA nao tem tributo nenhum a reproduzir: a mercadoria sai, mas nao ha
 * operacao de devolucao a demonstrar para o fornecedor se creditar.
 *
 * "Zerado" aqui e CAMPO VAZIO, e nao o numero zero — e a diferenca importa. Vazio vira
 * null e o campo some do XML; zero seria um valor DECLARADO de R$ 0,00. Se a intencao
 * fiscal for declarar zero, e trocar '' por '0' nesta funcao, num lugar so.
 */
/**
 * Campo vazio e campo "este item não tem esse tributo" eram a mesma coisa na tela, e não
 * são a mesma coisa. ST e IPI que a origem não traz passam a mostrar `0`: o item não tem
 * substituição, não tem IPI, e zero diz isso.
 *
 * MAS O ICMS CONTINUA EM BRANCO QUANDO FALTA, e isso é deliberado. Base, alíquota e valor
 * do ICMS existem em toda devolução — se vierem vazios é porque a origem não os tinha, e
 * é o operador que precisa preenchê-los. Zerá-los apagaria a coluna "Falta", que é
 * justamente o aviso de que há algo a digitar ali.
 *
 * Em simples remessa tudo vai a zero, porque não há tributo a reproduzir.
 */
const zeroOuVazio = (valor: number | null | undefined, zerar: boolean): string =>
  valor === null || valor === undefined ? (zerar ? '0' : '') : String(valor);

/**
 * PRODUTOR RURAL (contranota, script 246) também espelha a nota de origem, com duas
 * diferenças:
 * - o ICMS que a origem não tem nasce ZERO, e não vazio. A nota do produtor costuma vir com
 *   ICMS diferido (CST 51, sem base nem valor): aí zero é a informação, e com o campo vazio
 *   o serviço recusa ("alíquota obrigatória", como na nota 123);
 * - o IPI vai zerado: IPI devolvido só existe em nota de devolução.
 * O CFOP é o da contranota (1102/2102, compra para comercialização — script 247), que a prévia calcula pela UF do produtor.
 */
const montarEdicao = (lista: ItemPrevia[], operacao: string, cfopProdutor?: string): ItemEditavel[] => {
  const devolucao = operacao === 'DEVOLUCAO';
  const produtor = operacao === 'PRODUTOR_RURAL';
  const espelha = devolucao || produtor;

  return lista.map((i) => ({
    dvf_id: i.dvf_id,
    cfop: produtor ? comoTexto(cfopProdutor as any) : comoTexto(i.cfop_devolucao),
    quantidade: i.quantidade,

    /* ICMS: vazio quando a origem não tem, para a coluna "Falta" continuar avisando. */
    v_bc: espelha ? zeroOuVazio(i.v_bc, produtor) : '0',
    p_icms: espelha ? zeroOuVazio(i.p_icms, produtor) : '0',
    v_icms: espelha ? zeroOuVazio(i.v_icms, produtor) : '0',

    /* ST e IPI: zero quando a origem não tem, porque aí "não tem" é a informação. */
    v_bc_st: espelha ? zeroOuVazio(i.v_bc_st, true) : '0',
    p_icms_st: espelha ? zeroOuVazio(i.p_icms_st, true) : '0',
    v_icms_st: espelha ? zeroOuVazio(i.v_icms_st, true) : '0',
    v_ipi: devolucao ? zeroOuVazio(i.v_ipi, true) : '0',

    /* Outras despesas: o frete da origem. Zero quando a origem não tem frete — ou foi
       importada antes do script 224, que é quando o frete por item passou a ser gravado. */
    v_outro: espelha ? zeroOuVazio(i.v_outro, true) : '0',
  }));
};


/**
 * O que ainda falta NESTE item, pelo que está digitado agora.
 *
 * A prévia também devolve um `falta_o_que`, mas ele descreve a ORIGEM — o que a nota de
 * entrada não tinha. Era ele que a tela mostrava, e por isso o aviso não sumia depois de o
 * operador preencher o campo: a coluna dizia o que faltava ANTES, não o que falta AGORA.
 *
 * Em simples remessa não falta nada: os campos vão zerados de propósito.
 *
 * ST com valor zero é tratado como "não tem ST", e não como preenchido — zero é o que o
 * operador digita justamente para dizer que aquele item não tem substituição.
 */
const faltaNoItem = (e: ItemEditavel, operacao: string): string => {
  if (operacao === 'REMESSA') return '';

  const vazio = (v: string) => comoNumero(v) === null;
  const temST = (comoNumero(e.v_bc_st) ?? 0) > 0;

  const faltas: string[] = [];
  if (vazio(e.v_bc)) faltas.push('base do ICMS');
  if (vazio(e.p_icms)) faltas.push('alíquota do ICMS');
  if (vazio(e.v_icms)) faltas.push('valor do ICMS');
  if (temST && vazio(e.p_icms_st)) faltas.push('alíquota do ICMS-ST');
  if (temST && vazio(e.v_icms_st)) faltas.push('valor do ICMS-ST');

  return faltas.join('; ');
};

export const ModalComporNota = ({ aberto, dvfIds, aoFechar, aoGerar }: Props) => {
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [cabecalho, setCabecalho] = useState<any>(null);
  const [itens, setItens] = useState<ItemPrevia[]>([]);
  const [edicao, setEdicao] = useState<ItemEditavel[]>([]);

  const [tipoOperacao, setTipoOperacao] = useState('DEVOLUCAO');
  /* A operacao que o usuario escolheu e ainda nao confirmou. Enquanto nao confirmar, o
     campo continua mostrando a atual - trocar primeiro e perguntar depois deixaria a tela
     dizendo uma coisa e os valores outra. */
  const [trocaPendente, setTrocaPendente] = useState<string | null>(null);
  const [valorFrete, setValorFrete] = useState('0');
  const [modFrete, setModFrete] = useState('9');
  const [observacao, setObservacao] = useState('');

  /* Transportadora e volumes (script 244). Opcionais: vazios, o quadro do DANFE sai em
     branco como antes. A transportadora é uma linha de CLIENTE, buscada como no pedido. */
  const [transportador, setTransportador] = useState<SelectAutoCompleteOptions | null>(null);
  const [opcoesTransportador, setOpcoesTransportador] = useState<SelectAutoCompleteOptions[]>([]);
  const [quantidadeVolumes, setQuantidadeVolumes] = useState('');
  const [especie, setEspecie] = useState('');
  const [pesoBruto, setPesoBruto] = useState('');
  const [pesoLiquido, setPesoLiquido] = useState('');

  const buscarTransportador = async (texto: string) => {
    /* Apagar o nome desfaz a escolha: sem isso o id antigo iria na nota com o campo vazio. */
    if (!texto) {
      setTransportador(null);
      return;
    }
    if (texto === transportador?.label || texto.length < 2) return;

    const result = await ClienteController.ListarTransportador(texto);
    if (result.StatusCode === 200 && Array.isArray(result.Data)) {
      setOpcoesTransportador(
        result.Data.map((item: any) => ({ value: item.cli_id, label: item.cli_nome }))
      );
    }
  };

  useEffect(() => {
    if (!aberto || dvfIds.length === 0) return;

    const buscar = async () => {
      setCarregando(true);
      const response = await DevolucaoFornecedorController.Previa(dvfIds);
      setCarregando(false);

      if (response.StatusCode !== 200) {
        toast(response.ErroDetalhado ?? 'Não foi possível montar a prévia da nota.', {
          type: 'error',
        });
        aoFechar();
        return;
      }

      /* Resposta não-array trava a tela em branco — foi a causa do card de 23/10, e o
         ErrorBoundary só mostra o estrago depois. Aqui a lista cai para vazia. */
      const lista: ItemPrevia[] = Array.isArray(response.Data?.Itens) ? response.Data.Itens : [];

      setCabecalho(response.Data?.Cabecalho ?? null);
      setItens(lista);
      setEdicao(montarEdicao(lista, tipoOperacao, response.Data?.Cabecalho?.pr_cfop));
    };

    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, dvfIds.join(',')]);

  const alterar = (dvfId: number, campo: keyof ItemEditavel, valor: string) => {
    setEdicao((atual) =>
      atual.map((e) => (e.dvf_id === dvfId ? { ...e, [campo]: valor } : e))
    );
  };

  /* Trocar a operacao REFAZ as linhas a partir da previa, entao o que o operador digitou
     se perde. Por isso ele e avisado antes, e nao depois - e o aviso diz o que vai
     acontecer, nao "deseja continuar?". Selecionar a opcao que ja esta nao pergunta nada,
     porque nada mudaria. */
  const pedirTroca = (nova: string) => {
    if (nova === tipoOperacao) return;
    setTrocaPendente(nova);
  };

  const confirmarTroca = () => {
    if (!trocaPendente) return;
    setTipoOperacao(trocaPendente);
    setEdicao(montarEdicao(itens, trocaPendente, cabecalho?.pr_cfop));
    setTrocaPendente(null);
  };

  /* Os impedimentos, com o texto que o operador precisa ler. A ordem é a da gravidade:
     o que é da seleção vem antes do que é de cadastro, porque o primeiro ele resolve
     sozinho e o segundo depende de outra tela. */
  const impedimentos: string[] = [];
  if (cabecalho?.impede_varios_fornecedores === 'S')
    impedimentos.push('A seleção tem mais de um fornecedor. Uma nota fiscal tem um destinatário só.');
  if (cabecalho?.impede_ja_tem_nota === 'S')
    impedimentos.push('Há devolução que já está em outra nota fiscal.');
  if (cabecalho?.impede_nao_pendente === 'S')
    impedimentos.push('Há devolução que não está mais pendente.');
  if (cabecalho?.impede_sem_chave === 'S')
    impedimentos.push(
      'Há nota de entrada sem a chave de 44 dígitos. Sem ela a devolução não pode referenciá-la.'
    );
  if (cabecalho?.impede_sem_uf === 'S')
    impedimentos.push('O cadastro do fornecedor está sem UF. Complete o cadastro antes de emitir.');
  /* A contranota tem CFOP fixo (1102/2102), entao o aval do de-para nao se aplica a ela; no
     lugar entram as regras dela, que a gravacao tambem confere (script 246). */
  const produtorRural = tipoOperacao === 'PRODUTOR_RURAL';
  if (!produtorRural && cabecalho?.impede_cfop_sem_aval === 'S')
    impedimentos.push('Há CFOP de devolução ainda não aprovado pelo contador.');
  if (produtorRural && cabecalho?.impede_pr_varias_notas === 'S')
    impedimentos.push('A contranota é de uma nota de entrada só. A seleção tem registros de notas diferentes.');
  if (produtorRural && cabecalho?.impede_pr_ja_tem_contranota === 'S')
    impedimentos.push('Esta nota de entrada já tem uma contranota em vigor. Cancele ou exclua a anterior antes.');
  if (produtorRural && cabecalho?.pr_itens_faltando)
    impedimentos.push(
      `A contranota precisa da nota inteira, todos os itens nas quantidades cheias. Falta na seleção: ${cabecalho.pr_itens_faltando}.`
    );

  const podeGerar = !carregando && !salvando && itens.length > 0 && impedimentos.length === 0;

  const gerar = async () => {
    setSalvando(true);

    const response = await DevolucaoFornecedorController.GerarNotaFiscal({
      TipoOperacao: tipoOperacao,
      ValorFrete: comoNumero(valorFrete) ?? 0,
      ModFrete: modFrete,
      Observacao: observacao,
      TransportadorId: transportador?.value ?? null,
      QuantidadeVolumes: comoNumero(quantidadeVolumes),
      Especie: especie.trim() || null,
      PesoBruto: comoNumero(pesoBruto),
      PesoLiquido: comoNumero(pesoLiquido),
      Itens: edicao.map((e) => ({
        DvfId: e.dvf_id,
        Cfop: e.cfop,
        Quantidade: e.quantidade,
        VBc: comoNumero(e.v_bc),
        PIcms: comoNumero(e.p_icms),
        VIcms: comoNumero(e.v_icms),
        VBcSt: comoNumero(e.v_bc_st),
        PIcmsSt: comoNumero(e.p_icms_st),
        VIcmsSt: comoNumero(e.v_icms_st),
        VIpi: comoNumero(e.v_ipi),
        VOutro: comoNumero(e.v_outro),
      })),
    });

    setSalvando(false);

    if (response.StatusCode !== 200) {
      toast(response.ErroDetalhado ?? 'Não foi possível gerar a nota fiscal.', { type: 'error' });
      return;
    }

    /* O número vem LIDO DO BANCO pela procedure, e não ecoado do que foi enviado. A
       mensagem diz o que aconteceu de concreto: a nota existe e ainda não foi transmitida. */
    const numero = response.Data?.numero ?? response.Data?.nfe_id;
    toast(
      `Nota fiscal ${numero ?? ''} gerada. Ela ainda NÃO foi transmitida — use a Rotina de Nota Fiscal.`,
      { type: 'success' }
    );

    aoGerar();
  };

  const campo = (
    item: ItemEditavel,
    nome: keyof ItemEditavel,
    largura = 86
  ) => (
    <TextField
      size="small"
      value={item[nome] as string}
      onChange={(e) => alterar(item.dvf_id, nome, e.target.value)}
      inputProps={{ style: { textAlign: 'right', fontSize: 13, padding: '6px 8px' } }}
      sx={{ width: largura }}
    />
  );

  return (
    <Dialog open={aberto} onClose={aoFechar} maxWidth="xl" fullWidth>
      <DialogTitle>
        {produtorRural ? 'Compor contranota de produtor rural' : 'Compor nota fiscal de devolução'}
      </DialogTitle>

      <DialogContent dividers>
        {carregando && <Typography>Montando a prévia…</Typography>}

        {!carregando && cabecalho && (
          <>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Destinatário
                </Typography>
                <Typography>
                  <strong>{cabecalho.fornecedor ?? '(não identificado)'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cabecalho.fornecedor_cnpj ?? ''} · UF {cabecalho.fornecedor_uf ?? '—'} · IE{' '}
                  {cabecalho.fornecedor_ie ?? '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Registros
                </Typography>
                {/* "2 de 1 nota(s)" se lia como uma fracao. Sao duas contagens diferentes:
                    quantos registros entram na nota, e de quantas notas de origem eles vem. */}
                <Typography>
                  {cabecalho.registros} registro{cabecalho.registros === 1 ? '' : 's'}, de{' '}
                  {cabecalho.notas_origem} nota{cabecalho.notas_origem === 1 ? '' : 's'} de origem
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CFOP {cabecalho.cfops_origem ?? '—'} →{' '}
                  {produtorRural ? cabecalho.pr_cfop ?? '—' : cabecalho.cfop_devolucao ?? '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Valor dos produtos
                </Typography>
                <Typography>
                  <strong>{formatCurrency(cabecalho.valor_total ?? 0)}</strong>
                </Typography>
              </Box>
            </Box>

            {impedimentos.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {impedimentos.map((texto) => (
                  <div key={texto}>{texto}</div>
                ))}
              </Alert>
            )}

            {cabecalho.aviso_estoque_menor === 'S' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Há item cujo estoque atual é menor que a quantidade a devolver. A nota pode ser
                emitida; o saldo ficará negativo.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                select
                size="small"
                label="Operação"
                value={tipoOperacao}
                onChange={(e) => pedirTroca(e.target.value)}
                sx={{ width: 280 }}
              >
                <MenuItem value="DEVOLUCAO">Devolução (finalidade 4)</MenuItem>
                <MenuItem value="REMESSA">Simples remessa (finalidade 1)</MenuItem>
                <MenuItem value="PRODUTOR_RURAL">Produtor rural (contranota de entrada)</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Frete"
                value={modFrete}
                onChange={(e) => setModFrete(e.target.value)}
                sx={{ width: 220 }}
              >
                <MenuItem value="9">Sem frete</MenuItem>
                <MenuItem value="0">Por conta do emitente</MenuItem>
                <MenuItem value="1">Por conta do destinatário</MenuItem>
                <MenuItem value="2">Por conta de terceiros</MenuItem>
              </TextField>

              <TextField
                size="small"
                label="Valor do frete"
                value={valorFrete}
                onChange={(e) => setValorFrete(e.target.value)}
                inputProps={{ style: { textAlign: 'right' } }}
                sx={{ width: 140 }}
              />

              <TextField
                size="small"
                label="Observação (sai nos dados adicionais)"
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                sx={{ flex: 1, minWidth: 260 }}
              />
            </Box>

            {/* Transportadora e volumes — opcionais, saem no quadro TRANSPORTADOR/VOLUMES. */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <SelectAutoCompleteComponent
                  label="Transportadora (opcional)"
                  size="small"
                  value={transportador?.label ?? ''}
                  name="transportador_id"
                  options={opcoesTransportador}
                  handleSearch={buscarTransportador}
                  onSelectValue={(valor: SelectAutoCompleteOptions | null) =>
                    setTransportador(valor && typeof valor === 'object' ? valor : null)
                  }
                />
              </Box>

              <TextField
                size="small"
                label="Qtd. volumes"
                value={quantidadeVolumes}
                onChange={(e) => setQuantidadeVolumes(e.target.value.replace(/\D/g, ''))}
                inputProps={{ style: { textAlign: 'right' } }}
                sx={{ width: 120 }}
              />

              <TextField
                size="small"
                label="Espécie"
                value={especie}
                onChange={(e) => setEspecie(e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={{ width: 160 }}
              />

              <TextField
                size="small"
                label="Peso bruto (kg)"
                value={pesoBruto}
                onChange={(e) => setPesoBruto(e.target.value)}
                inputProps={{ style: { textAlign: 'right' } }}
                sx={{ width: 140 }}
              />

              <TextField
                size="small"
                label="Peso líquido (kg)"
                value={pesoLiquido}
                onChange={(e) => setPesoLiquido(e.target.value)}
                inputProps={{ style: { textAlign: 'right' } }}
                sx={{ width: 140 }}
              />
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell>NF origem</TableCell>
                  <TableCell align="right">Qtd.</TableCell>
                  <TableCell align="right">CFOP</TableCell>
                  <TableCell align="right">Base ICMS</TableCell>
                  <TableCell align="right">% ICMS</TableCell>
                  <TableCell align="right">ICMS</TableCell>
                  <TableCell align="right">Base ST</TableCell>
                  <TableCell align="right">% ST</TableCell>
                  <TableCell align="right">ICMS ST</TableCell>
                  <TableCell align="right">IPI devolvido</TableCell>
                  <TableCell align="right">Outras desp.</TableCell>
                  <TableCell>Falta</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {edicao.map((e) => {
                  const base = itens.find((i) => i.dvf_id === e.dvf_id);

                  return (
                    <TableRow key={e.dvf_id}>
                      <TableCell>
                        {base?.codigo_promatec} — {base?.produto}
                        {/* A descrição que sai na nota é a da origem; a do cadastro fica
                            acima para o operador reconhecer o produto. */}
                        {base?.descricao_origem && base.descricao_origem !== base.produto && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            Na nota: {base.descricao_origem}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {base?.nota_origem} / item {base?.numero_item_origem}
                      </TableCell>
                      <TableCell align="right">{base?.quantidade}</TableCell>
                      <TableCell align="right">{campo(e, 'cfop', 72)}</TableCell>
                      <TableCell align="right">{campo(e, 'v_bc')}</TableCell>
                      <TableCell align="right">{campo(e, 'p_icms', 70)}</TableCell>
                      <TableCell align="right">{campo(e, 'v_icms')}</TableCell>
                      <TableCell align="right">{campo(e, 'v_bc_st')}</TableCell>
                      <TableCell align="right">{campo(e, 'p_icms_st', 70)}</TableCell>
                      <TableCell align="right">{campo(e, 'v_icms_st')}</TableCell>
                      <TableCell align="right">{campo(e, 'v_ipi')}</TableCell>
                      <TableCell align="right">{campo(e, 'v_outro')}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'warning.main' }}>
                        {faltaNoItem(e, tipoOperacao)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

          </>
        )}
      </DialogContent>

      {/* A confirmacao da troca de operacao.

          Ela diz o que VAI ACONTECER - "os tributos serao zerados", "serao espelhados de
          novo" - e nao "deseja continuar?". E o mesmo padrao da confirmacao da tela 2: o
          operador decide melhor sabendo o efeito concreto do clique. */}
      <Dialog open={trocaPendente !== null} onClose={() => setTrocaPendente(null)} maxWidth="sm">
        <DialogTitle>Trocar a operação?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {trocaPendente === 'REMESSA'
              ? 'Em simples remessa não há tributo a reproduzir: os campos de ICMS, ICMS-ST, IPI e outras despesas de todos os itens ficarão zerados.'
              : trocaPendente === 'PRODUTOR_RURAL'
                ? `A contranota de produtor rural é uma nota de ENTRADA, com CFOP ${cabecalho?.pr_cfop ?? '1102/2102'}. Os tributos são espelhados da nota do produtor — o ICMS que ela não traz (diferido) fica zero — e o IPI fica zerado. Ela precisa da nota inteira e não mexe no estoque.`
                : 'Em devolução os tributos voltam a ser espelhados da nota de origem, item a item.'}
          </Typography>
          <Typography color="warning.main">
            <strong>As alterações que você já fez nos itens serão perdidas.</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            className=""
            variant="secondary"
            title="Cancelar"
            onClick={() => setTrocaPendente(null)}
          />
          <Button
            className=""
            variant="success"
            title={trocaPendente === 'REMESSA' ? 'Zerar e trocar' : 'Espelhar e trocar'}
            onClick={confirmarTroca}
          />
        </DialogActions>
      </Dialog>

      <DialogActions>
        <Button className="" variant="secondary" title="Fechar" onClick={aoFechar} />
        <Button
          className=""
          variant="success"
          title={salvando ? 'Gerando…' : 'Gerar nota fiscal'}
          disabled={!podeGerar}
          onClick={gerar}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ModalComporNota;
